import { useCallback, useEffect, useState } from 'react';

const SPOTLIGHT_PADDING = 8;

/**
 * Resolve a tour anchor element from a data-tour id.
 *
 * @param {string|null|undefined} tourTarget
 * @returns {HTMLElement|null}
 */
export const resolveTourAnchorElement = (tourTarget) => {
    if (!tourTarget || typeof document === 'undefined') {
        return null;
    }
    return document.querySelector(`[data-tour="${tourTarget}"]`);
};

/**
 * Measure a DOM rect with optional padding for spotlight cutout.
 *
 * @param {HTMLElement|null} element
 * @returns {{ top: number, left: number, width: number, height: number }|null}
 */
export const measureTourSpotlightRect = (element) => {
    if (!element) {
        return null;
    }
    const bounds = element.getBoundingClientRect();
    if (bounds.width <= 0 && bounds.height <= 0) {
        return null;
    }

    const top = Math.max(0, bounds.top - SPOTLIGHT_PADDING);
    const left = Math.max(0, bounds.left - SPOTLIGHT_PADDING);
    const right = Math.min(window.innerWidth, bounds.right + SPOTLIGHT_PADDING);
    const bottom = Math.min(window.innerHeight, bounds.bottom + SPOTLIGHT_PADDING);

    return {
        top,
        left,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
    };
};

/**
 * Track spotlight cutout geometry for a data-tour anchor.
 *
 * @param {string|null|undefined} tourTarget
 * @param {boolean} enabled
 * @returns {{ rect: object|null, targetElement: HTMLElement|null, refresh: () => void }}
 */
export const useTourSpotlightRect = (tourTarget, enabled) => {
    const [rect, setRect] = useState(null);
    const [targetElement, setTargetElement] = useState(null);

    const refresh = useCallback(() => {
        if (!enabled || !tourTarget) {
            setRect(null);
            setTargetElement(null);
            return;
        }

        const element = resolveTourAnchorElement(tourTarget);
        setTargetElement(element);
        setRect(measureTourSpotlightRect(element));
    }, [enabled, tourTarget]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!enabled || !tourTarget) {
            return undefined;
        }

        const element = resolveTourAnchorElement(tourTarget);
        if (element?.scrollIntoView) {
            element.scrollIntoView({ block: 'nearest', behavior: 'smooth', inline: 'nearest' });
        }

        const handleLayoutChange = () => {
            refresh();
        };

        window.addEventListener('resize', handleLayoutChange);
        window.addEventListener('scroll', handleLayoutChange, true);

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined' && element) {
            resizeObserver = new ResizeObserver(handleLayoutChange);
            resizeObserver.observe(element);
        }

        const retryTimer = window.setInterval(() => {
            const nextElement = resolveTourAnchorElement(tourTarget);
            if (nextElement) {
                refresh();
                window.clearInterval(retryTimer);
            }
        }, 400);

        const retryTimeout = window.setTimeout(() => {
            window.clearInterval(retryTimer);
        }, 5000);

        return () => {
            window.removeEventListener('resize', handleLayoutChange);
            window.removeEventListener('scroll', handleLayoutChange, true);
            resizeObserver?.disconnect();
            window.clearInterval(retryTimer);
            window.clearTimeout(retryTimeout);
        };
    }, [enabled, tourTarget, refresh]);

    return { rect, targetElement, refresh };
};
